function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(v => {
    const clamped = Math.min(255, Math.max(0, Math.round(v)));
    return clamped.toString(16).padStart(2, '0');
  }).join('');
}

function mixWithWhite(hex, weight) {
  const c = hexToRgb(hex);
  return rgbToHex(
    c.r + (255 - c.r) * weight,
    c.g + (255 - c.g) * weight,
    c.b + (255 - c.b) * weight,
  );
}

function darken(hex, amount) {
  const c = hexToRgb(hex);
  return rgbToHex(c.r * (1 - amount), c.g * (1 - amount), c.b * (1 - amount));
}

// Returns relative luminance (WCAG 2.1)
function luminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const toLinear = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

// Returns WCAG contrast ratio between two hex colors
export function contrastRatio(hex1, hex2) {
  const l1 = luminance(hex1);
  const l2 = luminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker  = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

// Returns '#ffffff' or '#1a1a1a' — whichever has better contrast over `bgHex`.
// Dark text must be at least 1.5× better than white to win; otherwise white is
// preferred. This prevents borderline medium-dark backgrounds (e.g. brand.600)
// from getting dark-on-dark text when the margin is too slim to look intentional.
export function readableTextColor(bgHex) {
  if (!bgHex || !bgHex.startsWith('#')) return '#1a1a1a';
  const onWhite = contrastRatio(bgHex, '#ffffff');
  const onDark  = contrastRatio(bgHex, '#1a1a1a');
  return (onDark >= 4.5 && onDark >= onWhite * 1.5) ? '#1a1a1a' : '#ffffff';
}

export function generateColorScale(hex) {
  if (!hex || !hex.startsWith('#')) return null;
  return {
    50:  mixWithWhite(hex, 0.95),
    100: mixWithWhite(hex, 0.88),
    200: mixWithWhite(hex, 0.75),
    300: mixWithWhite(hex, 0.55),
    400: mixWithWhite(hex, 0.30),
    500: hex,
    600: darken(hex, 0.12),
    700: darken(hex, 0.25),
    800: darken(hex, 0.38),
    900: darken(hex, 0.50),
  };
}
